import type {
  BillingSettings,
  BillingSummary,
  BillingLicensePlans,
  BillingBusinessLicense,
  BillingPaymentMethods,
  BillingCreateSetupIntent,
  BillingScheduledBusinessLicenses,
  BillingGetInvoicesParams,
  BillingGetInvoicesPage,
  BillingGetAIUsageCycles,
  BillingGetAIUsageSummary,
  BillingGetAIUsageSummaryParams,
  BillingGetAIUsageAgenticWorkflowsBreakdown,
  BillingGetAIUsageTeamMembersBreakdown,
  BillingGetAIUsageBreakdownParams,
} from '@kizen/api/billing';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithRequired, WithParams } from './types';

import {
  getBillingSettings,
  getSummaryUsage,
  getBillingLicensePlans,
  getBusinessLicense,
  getPaymentMethods,
  createSetupIntent,
  getScheduledBusinessLicenses,
  getInvoicesPage,
  getAIUsageCycles,
  getAIUsageSummary,
  getAIUsageAgenticWorkflowsBreakdown,
  getAIUsageTeamMembersBreakdown,
} from '@kizen/api/billing';
import { getResource } from './resource';
import { curry1 } from './utils';

type BillingInvoicesPageQuery<TData = BillingGetInvoicesPage> = WithRequired<
  QueryObserverOptions<
    BillingGetInvoicesPage,
    DefaultError,
    TData,
    BillingGetInvoicesPage,
    [Resource<Readonly<['billing', 'invoice']>, BillingGetInvoicesParams>]
  >,
  'queryFn' | 'queryKey'
>;

export const getInvoicesPageQuery = <TData = BillingGetInvoicesPage>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<BillingInvoicesPageQuery<TData>, 'queryFn' | 'queryKey'>,
    BillingGetInvoicesParams
  > = {}
): BillingInvoicesPageQuery<TData> => {
  const path = ['billing', 'invoice'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;
      return getInvoicesPage(axios, { signal, params: qk.params });
    },
    ...queryOptions,
  };
};

type BillingScheduledBusinessLicensesQuery<
  TData = BillingScheduledBusinessLicenses,
> = WithRequired<
  QueryObserverOptions<
    BillingScheduledBusinessLicenses,
    DefaultError,
    TData,
    BillingScheduledBusinessLicenses,
    [
      Resource<
        Readonly<['billing', 'business-license', 'scheduled-business-license']>
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const getScheduledBusinessLicensesQuery = <
  TData = BillingScheduledBusinessLicenses,
>(
  axios: AxiosInstance,
  options?: Omit<
    BillingScheduledBusinessLicensesQuery<TData>,
    'queryFn' | 'queryKey'
  >
): BillingScheduledBusinessLicensesQuery<TData> => {
  const path = [
    'billing',
    'business-license',
    'scheduled-business-license',
  ] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getScheduledBusinessLicenses(axios, { signal });
    },
    ...options,
  };
};

type BillingCreateSetupIntentQuery<TData = BillingCreateSetupIntent> =
  WithRequired<
    QueryObserverOptions<
      BillingCreateSetupIntent,
      DefaultError,
      TData,
      BillingCreateSetupIntent,
      [Resource<Readonly<['billing', 'create-setup-intent']>>]
    >,
    'queryFn' | 'queryKey'
  >;

export const createSetupIntentQuery = <TData = BillingCreateSetupIntent>(
  axios: AxiosInstance,
  options?: Omit<BillingCreateSetupIntentQuery<TData>, 'queryFn' | 'queryKey'>
): BillingCreateSetupIntentQuery<TData> => {
  const path = ['billing', 'create-setup-intent'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return createSetupIntent(axios, { signal });
    },
    ...options,
  };
};

type BillingPaymentMethodsQuery<TData = BillingPaymentMethods> = WithRequired<
  QueryObserverOptions<
    BillingPaymentMethods,
    DefaultError,
    TData,
    BillingPaymentMethods,
    [Resource<Readonly<['billing', 'payment-methods']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getPaymentMethodsQuery = <TData = BillingPaymentMethods>(
  axios: AxiosInstance,
  options?: Omit<BillingPaymentMethodsQuery<TData>, 'queryFn' | 'queryKey'>
): BillingPaymentMethodsQuery<TData> => {
  const path = ['billing', 'payment-methods'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getPaymentMethods(axios, { signal });
    },
    ...options,
  };
};

type BillingSummaryQuery<TData = BillingSummary> = WithRequired<
  QueryObserverOptions<
    BillingSummary,
    DefaultError,
    TData,
    BillingSummary,
    [Resource<Readonly<['billing', 'summary-usage']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getSummaryUsageQuery = <TData = BillingSummary>(
  axios: AxiosInstance,
  options?: Omit<BillingSummaryQuery<TData>, 'queryFn' | 'queryKey'>
): BillingSummaryQuery<TData> => {
  const path = ['billing', 'summary-usage'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getSummaryUsage(axios, { signal });
    },
    ...options,
  };
};

type BillingLicensePlansQuery<TData = BillingLicensePlans> = WithRequired<
  QueryObserverOptions<
    BillingLicensePlans,
    DefaultError,
    TData,
    BillingLicensePlans,
    [Resource<Readonly<['billing', 'license-plans']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getBillingLicensePlansQuery = <TData = BillingLicensePlans>(
  axios: AxiosInstance,
  options?: Omit<BillingLicensePlansQuery<TData>, 'queryFn' | 'queryKey'>
): BillingLicensePlansQuery<TData> => {
  const path = ['billing', 'license-plans'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getBillingLicensePlans(axios, { signal });
    },
    ...options,
  };
};

type BillingBusinessLicenseQuery<TData = BillingBusinessLicense> = WithRequired<
  QueryObserverOptions<
    BillingBusinessLicense,
    DefaultError,
    TData,
    BillingBusinessLicense,
    [Resource<Readonly<['billing', 'business-license']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getBusinessLicenseQuery = <TData = BillingBusinessLicense>(
  axios: AxiosInstance,
  options?: Omit<BillingBusinessLicenseQuery<TData>, 'queryFn' | 'queryKey'>
): BillingBusinessLicenseQuery<TData> => {
  const path = ['billing', 'business-license'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getBusinessLicense(axios, { signal });
    },
    ...options,
  };
};

type BillingSettingsQuery<TData = BillingSettings> = WithRequired<
  QueryObserverOptions<
    BillingSettings,
    DefaultError,
    TData,
    BillingSettings,
    [Resource<Readonly<['billing', 'settings']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getBillingSettingsQuery = <TData = BillingSettings>(
  axios: AxiosInstance,
  options?: Omit<BillingSettingsQuery<TData>, 'queryFn' | 'queryKey'>
): BillingSettingsQuery<TData> => {
  const path = ['billing', 'settings'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getBillingSettings(axios, { signal });
    },
    ...options,
  };
};

type AIUsageCyclesQuery<TData = BillingGetAIUsageCycles> = WithRequired<
  QueryObserverOptions<
    BillingGetAIUsageCycles,
    DefaultError,
    TData,
    BillingGetAIUsageCycles,
    [Resource<Readonly<['billing', 'ai-usage-cycles']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const getAIUsageCyclesQuery = <TData = BillingGetAIUsageCycles>(
  axios: AxiosInstance,
  options?: Omit<AIUsageCyclesQuery<TData>, 'queryFn' | 'queryKey'>
): AIUsageCyclesQuery<TData> => {
  const path = ['billing', 'ai-usage-cycles'] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getAIUsageCycles(axios, { signal });
    },
    ...options,
  };
};

type AIUsageSummaryQuery<TData = BillingGetAIUsageSummary> = WithRequired<
  QueryObserverOptions<
    BillingGetAIUsageSummary,
    DefaultError,
    TData,
    BillingGetAIUsageSummary,
    [
      Resource<
        Readonly<['billing', 'ai-usage-summary']>,
        BillingGetAIUsageSummaryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const getAIUsageSummaryQuery = <TData = BillingGetAIUsageSummary>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<AIUsageSummaryQuery<TData>, 'queryFn' | 'queryKey'>,
    BillingGetAIUsageSummaryParams
  > = {}
): AIUsageSummaryQuery<TData> => {
  const path = ['billing', 'ai-usage-summary'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;
      return getAIUsageSummary(axios, { signal, params: qk.params });
    },
    ...queryOptions,
  };
};

type AIUsageAgenticWorkflowsBreakdownQuery<
  TData = BillingGetAIUsageAgenticWorkflowsBreakdown,
> = WithRequired<
  QueryObserverOptions<
    BillingGetAIUsageAgenticWorkflowsBreakdown,
    DefaultError,
    TData,
    BillingGetAIUsageAgenticWorkflowsBreakdown,
    [
      Resource<
        Readonly<['billing', 'ai-usage-summary', 'agentic-workflows']>,
        BillingGetAIUsageBreakdownParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const getAIUsageAgenticWorkflowsBreakdownQuery = <
  TData = BillingGetAIUsageAgenticWorkflowsBreakdown,
>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<AIUsageAgenticWorkflowsBreakdownQuery<TData>, 'queryFn' | 'queryKey'>,
    BillingGetAIUsageBreakdownParams
  > = {}
): AIUsageAgenticWorkflowsBreakdownQuery<TData> => {
  const path = ['billing', 'ai-usage-summary', 'agentic-workflows'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;
      return getAIUsageAgenticWorkflowsBreakdown(axios, {
        signal,
        params: qk.params,
      });
    },
    ...queryOptions,
  };
};

type AIUsageTeamMembersBreakdownQuery<
  TData = BillingGetAIUsageTeamMembersBreakdown,
> = WithRequired<
  QueryObserverOptions<
    BillingGetAIUsageTeamMembersBreakdown,
    DefaultError,
    TData,
    BillingGetAIUsageTeamMembersBreakdown,
    [
      Resource<
        Readonly<['billing', 'ai-usage-summary', 'team-members']>,
        BillingGetAIUsageBreakdownParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const getAIUsageTeamMembersBreakdownQuery = <
  TData = BillingGetAIUsageTeamMembersBreakdown,
>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<AIUsageTeamMembersBreakdownQuery<TData>, 'queryFn' | 'queryKey'>,
    BillingGetAIUsageBreakdownParams
  > = {}
): AIUsageTeamMembersBreakdownQuery<TData> => {
  const path = ['billing', 'ai-usage-summary', 'team-members'] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;
      return getAIUsageTeamMembersBreakdown(axios, {
        signal,
        params: qk.params,
      });
    },
    ...queryOptions,
  };
};

class Queries {
  public getSummaryUsageQuery: <TData = BillingSummary>(
    ...args: Drop<Parameters<typeof getSummaryUsageQuery<TData>>>
  ) => BillingSummaryQuery<TData>;

  public getBillingLicensePlansQuery: <TData = BillingLicensePlans>(
    ...args: Drop<Parameters<typeof getBillingLicensePlansQuery<TData>>>
  ) => BillingLicensePlansQuery<TData>;

  public getBusinessLicenseQuery: <TData = BillingBusinessLicense>(
    ...args: Drop<Parameters<typeof getBusinessLicenseQuery<TData>>>
  ) => BillingBusinessLicenseQuery<TData>;

  public getPaymentMethodsQuery: <TData = BillingPaymentMethods>(
    ...args: Drop<Parameters<typeof getPaymentMethodsQuery<TData>>>
  ) => BillingPaymentMethodsQuery<TData>;

  public createSetupIntentQuery: <TData = BillingCreateSetupIntent>(
    ...args: Drop<Parameters<typeof createSetupIntentQuery<TData>>>
  ) => BillingCreateSetupIntentQuery<TData>;

  public getScheduledBusinessLicensesQuery: <
    TData = BillingScheduledBusinessLicenses,
  >(
    ...args: Drop<Parameters<typeof getScheduledBusinessLicensesQuery<TData>>>
  ) => BillingScheduledBusinessLicensesQuery<TData>;

  public getBillingSettingsQuery: <TData = BillingSettings>(
    ...args: Drop<Parameters<typeof getBillingSettingsQuery<TData>>>
  ) => BillingSettingsQuery<TData>;

  public getInvoicesPageQuery: <TData = BillingGetInvoicesPage>(
    ...args: Drop<Parameters<typeof getInvoicesPageQuery<TData>>>
  ) => BillingInvoicesPageQuery<TData>;

  public getAIUsageCyclesQuery: <TData = BillingGetAIUsageCycles>(
    ...args: Drop<Parameters<typeof getAIUsageCyclesQuery<TData>>>
  ) => AIUsageCyclesQuery<TData>;

  public getAIUsageSummaryQuery: <TData = BillingGetAIUsageSummary>(
    ...args: Drop<Parameters<typeof getAIUsageSummaryQuery<TData>>>
  ) => AIUsageSummaryQuery<TData>;

  public getAIUsageAgenticWorkflowsBreakdownQuery: <
    TData = BillingGetAIUsageAgenticWorkflowsBreakdown,
  >(
    ...args: Drop<
      Parameters<typeof getAIUsageAgenticWorkflowsBreakdownQuery<TData>>
    >
  ) => AIUsageAgenticWorkflowsBreakdownQuery<TData>;

  public getAIUsageTeamMembersBreakdownQuery: <
    TData = BillingGetAIUsageTeamMembersBreakdown,
  >(
    ...args: Drop<Parameters<typeof getAIUsageTeamMembersBreakdownQuery<TData>>>
  ) => AIUsageTeamMembersBreakdownQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.getSummaryUsageQuery = curry1(getSummaryUsageQuery)(axios);
    this.getBillingLicensePlansQuery = curry1(getBillingLicensePlansQuery)(
      axios
    );
    this.getBusinessLicenseQuery = curry1(getBusinessLicenseQuery)(axios);
    this.getPaymentMethodsQuery = curry1(getPaymentMethodsQuery)(axios);
    this.createSetupIntentQuery = curry1(createSetupIntentQuery)(axios);
    this.getScheduledBusinessLicensesQuery = curry1(
      getScheduledBusinessLicensesQuery
    )(axios);
    this.getBillingSettingsQuery = curry1(getBillingSettingsQuery)(axios);
    this.getInvoicesPageQuery = curry1(getInvoicesPageQuery)(axios);
    this.getAIUsageCyclesQuery = curry1(getAIUsageCyclesQuery)(axios);
    this.getAIUsageSummaryQuery = curry1(getAIUsageSummaryQuery)(axios);
    this.getAIUsageAgenticWorkflowsBreakdownQuery = curry1(
      getAIUsageAgenticWorkflowsBreakdownQuery
    )(axios);
    this.getAIUsageTeamMembersBreakdownQuery = curry1(
      getAIUsageTeamMembersBreakdownQuery
    )(axios);
  }
}

export type BillingQueries = Queries;

export const defBillingQueries = (axios: AxiosInstance): BillingQueries => {
  return new Queries(axios);
};
